import { supabase } from '../lib/supabase';

export interface SendNotificationPayload {
  user_id?: string;
  user_ids?: string[];
  title: string;
  body: string;
  data?: Record<string, any>;
  notification_type: NotificationType;
  priority?: 'normal' | 'high';
  sound?: string;
  badge?: number;
  click_action?: string;
  icon?: string;
  color?: string;
  subtitle?: string;
  channel_id?: string;
  sender_id?: string;
}

export type NotificationType = 
  | 'order_status'
  | 'delivery_update'
  | 'seller_confirmation'
  | 'chat_message'
  | 'payment_update'
  | 'system_announcement'
  | 'promotional'
  | 'new_order'
  | 'new_review'
  | 'rate_purchase';

export interface NotificationResponse {
  message: string;
  user_count: number;
  token_count: number;
  push_results?: {
    tickets: Array<{
      token: string;
      status: string;
      id?: string;
      message?: string;
      details?: Record<string, any>;
    }>;
    errors?: Array<Record<string, any>>;
  };
}

class NotificationService {
  private readonly functionUrl: string;

  constructor() {
    // Get the Supabase URL and construct the edge function URL
    const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL || 'https://wgghxlrehnnejmvzzsmx.supabase.co';
    this.functionUrl = `${supabaseUrl}/functions/v1/send-notification`;
  }

  /**
   * Send notification to a single user
   */
  async sendToUser(
    userId: string,
    notification: Omit<SendNotificationPayload, 'user_id' | 'user_ids'>
  ): Promise<NotificationResponse> {
    const payload: SendNotificationPayload = {
      user_id: userId,
      ...notification,
    };

    return this.sendNotification(payload);
  }

  /**
   * Send notification to multiple users
   */
  async sendToUsers(
    userIds: string[],
    notification: Omit<SendNotificationPayload, 'user_id' | 'user_ids'>
  ): Promise<NotificationResponse> {
    if (userIds.length === 0) {
      throw new Error('No user IDs provided');
    }

    const payload: SendNotificationPayload = {
      user_ids: userIds,
      ...notification,
    };

    return this.sendNotification(payload);
  }

  /**
   * Send order status notification
   */
  async sendOrderStatusNotification(
    userId: string,
    orderId: string,
    status: string,
    customMessage?: string
  ): Promise<NotificationResponse> {
    const statusMessages: Record<string, string> = {
      pending: 'Your order is pending approval',
      confirmed: 'Your order has been confirmed',
      paid: 'Your order has been received',
      wrapping_up: 'Your order is being prepared',
      packed: 'Your order has been packed',
      pickup_pending: 'A courier is on the way to pick up your order',
      pickup_complete: 'Your order has been picked up',
      in_transit: 'Your order is on the way',
      delivered: 'Your order has been delivered',
      completed: 'Your order has been completed',
      cancelled: 'Your order has been cancelled',
      rejected: 'Your order has been cancelled',
      failed: 'There was an issue with your order',
      preparing: 'Your order is being prepared',
      ready: 'Your order is ready for pickup',
      out_for_delivery: 'Your order is out for delivery',
    };

    // Helper to convert snake_case to Title Case
    const formatStatus = (s: string) => s
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    const title = 'Order Update';
    const body = customMessage || statusMessages[status] || `Order status: ${formatStatus(status)}`;

    return this.sendToUser(userId, {
      title,
      body,
      notification_type: 'order_status',
      priority: 'high',
      sound: 'default',
      data: {
        orderId,
        status,
        type: 'order_status',
      },
    });
  }

  /**
   * Send delivery update notification
   */
  async sendDeliveryUpdateNotification(
    userId: string,
    deliveryId: string,
    status: string,
    estimatedTime?: string
  ): Promise<NotificationResponse> {
    const statusMessages: Record<string, string> = {
      pending: 'Your delivery is being prepared',
      assigned: 'A driver has been assigned to your delivery',
      pickup_pending: 'A courier is on the way to pick up your order',
      pickup_complete: 'Your order has been picked up',
      picked_up: 'Your order has been picked up',
      in_transit: 'Your order is on the way',
      dropoff: 'Your order is arriving soon',
      delivered: 'Your order has been delivered',
      completed: 'Your delivery has been completed',
      cancelled: 'Your delivery has been cancelled',
      failed: 'Delivery attempt failed',
    };

    // Helper to convert snake_case to Title Case
    const formatStatus = (s: string) => s
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    const title = 'Delivery Update';
    const body = statusMessages[status] || `Delivery status: ${formatStatus(status)}`;
    const finalBody = estimatedTime ? `${body}. ETA: ${estimatedTime}` : body;

    return this.sendToUser(userId, {
      title,
      body: finalBody,
      notification_type: 'delivery_update',
      priority: 'high',
      sound: 'default',
      data: {
        deliveryId,
        status,
        estimatedTime,
        type: 'delivery_update',
      },
    });
  }

  /**
   * Send seller confirmation request notification
   */
  async sendSellerConfirmationNotification(
    sellerId: string,
    requestId: string,
    buyerName: string,
    productName: string
  ): Promise<NotificationResponse> {
    const title = 'New Order Request';
    const body = `${buyerName} wants to buy ${productName}. Please confirm availability.`;

    return this.sendToUser(sellerId, {
      title,
      body,
      notification_type: 'seller_confirmation',
      priority: 'high',
      sound: 'default',
      data: {
        requestId,
        buyerName,
        productName,
        type: 'seller_confirmation',
      },
    });
  }

  /**
   * Send chat message notification
   */
  async sendChatMessageNotification(
    userId: string,
    chatId: string,
    senderName: string,
    message: string
  ): Promise<NotificationResponse> {
    const title = 'New Message';
    const body = message.length > 100 ? `${message.substring(0, 97)}...` : message;

    return this.sendToUser(userId, {
      title,
      body,
      notification_type: 'chat_message',
      // Use high priority for Android - normal priority can be delayed/batched
      priority: 'high',
      sound: 'default',
      data: {
        chatId,
        senderName,
        type: 'chat_message',
      },
    });
  }

  /**
   * Send payment update notification
   */
  async sendPaymentUpdateNotification(
    userId: string,
    paymentId: string,
    status: string,
    amount?: number
  ): Promise<NotificationResponse> {
    const statusMessages: Record<string, string> = {
      pending: 'Your payment is pending',
      processing: 'Your payment is being processed',
      succeeded: 'Payment completed successfully',
      completed: 'Payment completed successfully',
      failed: 'Payment failed - please try again',
      refunded: 'Your payment has been refunded',
      cancelled: 'Your payment has been cancelled',
    };

    // Helper to convert snake_case to Title Case
    const formatStatus = (s: string) => s
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    const title = 'Payment Update';
    const body = statusMessages[status] || `Payment status: ${formatStatus(status)}`;
    const finalBody = amount ? `${body} - $${amount.toFixed(2)}` : body;

    return this.sendToUser(userId, {
      title,
      body: finalBody,
      notification_type: 'payment_update',
      priority: 'high',
      sound: 'default',
      data: {
        paymentId,
        status,
        amount,
        type: 'payment_update',
      },
    });
  }

  /**
   * Send new order notification to seller
   */
  async sendNewOrderNotification(
    sellerId: string,
    orderId: string,
    vendorOrderId: string,
    buyerName: string,
    itemCount: number,
    totalAmount: number
  ): Promise<NotificationResponse> {
    const title = 'New Order Received!';
    const itemText = itemCount === 1 ? 'item' : 'items';
    const body = `${buyerName} just purchased ${itemCount} ${itemText} for $${totalAmount.toFixed(2)}`;

    return this.sendToUser(sellerId, {
      title,
      body,
      notification_type: 'new_order',
      priority: 'high',
      sound: 'default',
      data: {
        orderId,
        vendorOrderId,
        buyerName,
        itemCount,
        totalAmount,
        type: 'new_order',
      },
    });
  }

  /**
   * Send system announcement to all users
   */
  async sendSystemAnnouncement(
    userIds: string[],
    title: string,
    body: string,
    data?: Record<string, any>
  ): Promise<NotificationResponse> {
    return this.sendToUsers(userIds, {
      title,
      body,
      notification_type: 'system_announcement',
      priority: 'high',
      sound: 'default',
      data: {
        type: 'system_announcement',
        ...data,
      },
    });
  }

  /**
   * Send promotional notification
   */
  async sendPromotionalNotification(
    userIds: string[],
    title: string,
    body: string,
    promoCode?: string,
    data?: Record<string, any>
  ): Promise<NotificationResponse> {
    return this.sendToUsers(userIds, {
      title,
      body,
      notification_type: 'promotional',
      priority: 'high',
      sound: 'default',
      data: {
        type: 'promotional',
        promoCode,
        ...data,
      },
    });
  }

  /**
   * Send notification to seller when they receive a new review
   */
  async sendNewReviewNotification(
    sellerId: string,
    productTitle: string,
    rating: number,
    feedback?: string | null,
    vendorOrderId?: string | null
  ): Promise<NotificationResponse> {
    const stars = '⭐'.repeat(rating);
    const title = 'New Review Received!';
    const body = feedback 
      ? `${stars} for "${productTitle}" - "${feedback.substring(0, 50)}${feedback.length > 50 ? '...' : ''}"`
      : `${stars} for "${productTitle}"`;

    return this.sendToUser(sellerId, {
      title,
      body,
      notification_type: 'new_review',
      priority: 'normal',
      sound: 'default',
      data: {
        type: 'new_review',
        productTitle,
        rating,
        feedback,
        vendorOrderId,
      },
    });
  }

  /**
   * Send notification to buyer to rate their purchase after delivery
   */
  async sendRatePurchaseNotification(
    buyerId: string,
    orderId: string,
    vendorName?: string
  ): Promise<NotificationResponse> {
    const title = 'How was your order?';
    const body = vendorName 
      ? `Your order from ${vendorName} has been delivered! Tap to leave a review.`
      : 'Your order has been delivered! Tap to leave a review.';

    return this.sendToUser(buyerId, {
      title,
      body,
      notification_type: 'rate_purchase',
      priority: 'normal',
      sound: 'default',
      data: {
        type: 'rate_purchase',
        orderId,
        vendorName,
      },
    });
  }

  /**
   * Core method to send notification via Supabase edge function
   */
  private async sendNotification(payload: SendNotificationPayload): Promise<NotificationResponse> {
    try {
      console.log('📤 [NotificationService] Sending notification:', {
        type: payload.notification_type,
        title: payload.title,
        targetUserId: payload.user_id,
        targetUserIds: payload.user_ids,
        userCount: payload.user_id ? 1 : payload.user_ids?.length || 0,
        functionUrl: this.functionUrl,
      });

      // Get the current session to use for authentication
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.error('❌ [NotificationService] No authenticated session found - cannot send notification');
        throw new Error('No authenticated session found');
      }

      console.log('📤 [NotificationService] Calling edge function with auth token...');

      const response = await fetch(this.functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      let result: NotificationResponse;
      
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ [NotificationService] Failed to parse response:', responseText);
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`);
      }

      if (!response.ok) {
        console.error('❌ [NotificationService] Edge function returned error:', {
          status: response.status,
          result,
        });
        throw new Error(`HTTP ${response.status}: ${(result as any).error || 'Failed to send notification'}`);
      }
      
      const successCount =
        result.push_results?.tickets?.filter(ticket => ticket.status === 'ok').length ?? 0;
      const failureCount =
        result.push_results?.tickets?.filter(ticket => ticket.status !== 'ok').length ?? 0;

      // Log detailed results for debugging Android issues
      console.log('✅ [NotificationService] Notification sent:', {
        userCount: result.user_count,
        tokenCount: result.token_count,
        success: successCount,
        failure: failureCount,
        message: result.message,
      });

      // If no tokens were found, log a warning
      if (result.token_count === 0) {
        console.warn('⚠️ [NotificationService] NO PUSH TOKENS FOUND for target user(s)!', {
          targetUserId: payload.user_id,
          targetUserIds: payload.user_ids,
          hint: 'The recipient(s) may not have registered for push notifications on their device.',
        });
      }

      // Log any failed deliveries
      if (failureCount > 0) {
        const failedTickets = result.push_results?.tickets?.filter(t => t.status !== 'ok') || [];
        console.warn('⚠️ [NotificationService] Some notifications failed to deliver:', {
          failedCount: failureCount,
          failures: failedTickets.map(t => ({
            tokenPrefix: t.token?.substring(0, 25) + '...',
            status: t.status,
            message: t.message,
            details: t.details,
          })),
        });
      }

      return result;

    } catch (error) {
      console.error('❌ [NotificationService] Failed to send notification:', error);
      throw error;
    }
  }

  /**
   * Get user's notification history
   */
  async getUserNotifications(userId: string, limit: number = 50) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('sent_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('❌ Failed to get user notifications:', error);
      throw error;
    }
  }

  /**
   * Mark notification as read
   */
  async markNotificationRead(notificationId: string) {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('❌ Failed to mark notification as read:', error);
      throw error;
    }
  }

  /**
   * Get unread notification count for user
   */
  async getUnreadCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .is('read_at', null);

      if (error) throw error;

      return count || 0;
    } catch (error) {
      console.error('❌ Failed to get unread count:', error);
      return 0;
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;